import { useMemo } from 'react';

import { blockSelectors, relationsSelectors } from '../../slices';
import { distributeAndSortItemsByPosition } from '../lib';

import type { Point } from '#shared';
import type { WidgetProps } from '../../widgetManager';

type HelperItem = { id: string; donorHospital?: { lonLat?: Point } };

export interface WidgetDistributionOptions {
  /** Total number of widget instances for horizontal distribution */
  totalInstances?: number;
  /** Current instance index (0-based) for horizontal distribution */
  instanceIndex?: number;
}

/**
 * Hook for distributing items across multiple widget instances
 * - For vertical layout: Distributes between left/right sections based on geo position
 * - For horizontal layout: Distributes items equally between instances
 */
export const useWidgetItemsDistribution = <
  T extends { id: string; donorHospital?: { lonLat?: Point | null } | null },
>(
  allItems: readonly T[],
  instance: WidgetProps['instance'],
  variant: 'horizontal' | 'vertical',
  maxItemsPerSide?: number,
  options?: WidgetDistributionOptions,
) => {
  const { totalInstances = 1, instanceIndex = 0 } = options || {};

  const newCasesCoordinates = relationsSelectors.useSelectNewCasesOnMap();
  const geoMapCenter = relationsSelectors.useSelectGeoMapCenter();

  // Check if newCases blocks are visible (replaces section enabled checks)
  const newCasesBlocksVisible = blockSelectors.useSelectNewCasesBlocksVisible();

  return useMemo(() => {
    // Handle horizontal distribution (equally divide items between instances)
    if (
      variant === 'horizontal' &&
      totalInstances > 1 &&
      instanceIndex < totalInstances
    ) {
      const totalItems = allItems.length;
      const itemsPerInstance = Math.floor(totalItems / totalInstances);
      const remainderItems = totalItems % totalInstances;

      // Calculate start and end indices for this instance
      // Distribute remainder items one per instance from the beginning
      const extraItem = instanceIndex < remainderItems ? 1 : 0;
      const start =
        instanceIndex * itemsPerInstance +
        Math.min(instanceIndex, remainderItems);
      const end = start + itemsPerInstance + extraItem;

      // Get items for this instance
      const items = allItems.slice(start, end);

      return {
        items,
        meta: {
          start,
          end,
          count: items.length,
          total: totalItems,
          copies: totalInstances,
          index: instanceIndex,
        },
      };
    }

    // Default behavior for horizontal with single instance
    if (variant === 'horizontal' || !newCasesBlocksVisible) {
      return {
        items: allItems,
        meta: {
          start: 0,
          end: allItems.length,
          count: allItems.length,
          total: allItems.length,
          copies: 1,
          index: 0,
        },
      };
    }

    // Handle vertical distribution (left/right)
    // Map originals by id to return exact T's after helper
    const byId = new Map(allItems.map(i => [i.id, i]));

    // sanitize: convert nulls to undefined so helper sees correct shape
    const itemsForHelper: ReadonlyArray<HelperItem> = allItems.map(i => {
      const lonLat = i.donorHospital?.lonLat ?? undefined;
      return lonLat ? { id: i.id, donorHospital: { lonLat } } : { id: i.id }; // omit donorHospital if lonLat not present
    });

    const { leftItems, rightItems } = distributeAndSortItemsByPosition(
      itemsForHelper,
      newCasesCoordinates,
      maxItemsPerSide,
      geoMapCenter,
    );

    // Use instanceIndex to determine which items to show (0=left, 1=right)
    // Fallback to showing all items if instanceIndex is not defined
    const instanceIdx =
      'instanceIndex' in instance && typeof instance.instanceIndex === 'number'
        ? instance.instanceIndex
        : undefined;
    const pick =
      instanceIdx === 0
        ? leftItems
        : instanceIdx === 1
          ? rightItems
          : leftItems;
    const items = pick
      .map(i => byId.get(i.id))
      .filter((v): v is T => Boolean(v));

    return {
      items,
      meta: {
        start: 0,
        end: items.length,
        count: items.length,
        total: allItems.length,
        copies: 2,
        index: instanceIdx ?? 0,
      },
    };
  }, [
    allItems,
    variant,
    maxItemsPerSide,
    newCasesCoordinates,
    geoMapCenter,
    totalInstances,
    instanceIndex,
    instance,
    newCasesBlocksVisible,
  ]);
};
